<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { DsIconButton, DsButtonPlay, dsUseUniqueId, DsButton } from '@coloplast/design-system';
import { youtubeVideoUrl } from 'shared/services/urlTypeService';
import { track_content_engagement } from 'shared/services/tracking/contentTrackingService';
import "@coloplast/design-system/dist/components/button/ds-button-play.css";

const props = defineProps({
	imageUrl: String,
	imageAlt: String,
	videoUrl: String,
	videoTitle: String,
	imageRoundedCorner: Boolean,
	objectContain: Boolean,
	mobileImageUrl: String,
	videoLoop: Boolean,
	playAriaLabel: String,
	pauseAriaLabel: String,
	videoPlayButtonLabel: String,
});

const uniqueId = dsUseUniqueId('split-media-block-video');

let videoPlayer;
const areScriptsLoaded = ref(false);
const video = ref();
const isVideoPlayingOnLoad = props.videoLoop; //currently there is no prop for that, and only way for 'true' is when video loop is on
const isVideoPlaying = ref(false);
const isVideoPaused = ref(false);
const isPlayerFullyLoaded = ref(false);
const isOnReadySent = ref(false);
const isVimeo = computed(() => props.videoUrl.indexOf('vimeo.com') !== -1);
const isVimeoInPopup = computed(() => isVimeo.value && !props.videoLoop)
const videoUrl = ref(null);
const intervalTimer = ref(null);
const isPlayerCreated = ref(false);
const isPlayerWithLoopReady = computed(() => !isVimeoInPopup.value && isPlayerCreated.value && isOnReadySent.value && isPlayerFullyLoaded.value && props.videoLoop);
const showPlayLoopButton = computed(() => isPlayerWithLoopReady.value && isVideoPaused.value);
const showPauseLoopButton = computed(() => isPlayerWithLoopReady.value && isVideoPlaying.value && !isVideoPaused.value);

const embedTemplate = isVimeo.value ? 'https://player.vimeo.com/video/' : youtubeVideoUrl();

const embedVideoId = computed(() => {
	if (!videoUrl.value || !props.videoUrl) return null;

	if (isVimeo.value) {
		const id = props.videoUrl.split('/').pop();
		return isVimeoInPopup.value ? `${id}?autoplay=1&api=1` : `${id}`;
	}

	const id = props.videoUrl.indexOf('youtu.be') !== -1;
	let m = !id
		? /watch\?v=([^$]*)/.exec(props.videoUrl)
		: /youtu.be\/([^$]*)/.exec(props.videoUrl);
	if (m) {
		m = m[1].split('&');
		m = m[0].split('?');
		return m[0];
	}
});

const embedVideoParams = computed(() => {
	const autoplay = `autoplay=${isVideoPlayingOnLoad ? '1' : '0'}`;
	const loop = `${props.videoLoop ? '&loop=1&playlist=' + embedVideoId.value : '&loop=0'}`;
	const dnt = window.marketingPermissions ? '' : '&dnt=true';
	const muted = isVimeo.value ? '&muted=1' : '&mute=1'; //must have for autoplay=1
	const playsinline = '&playsinline=1';
	const controls = `&controls=${props.videoLoop ? '0' : '1'}`;

	const youtubeSpecificParams = `&fs=0&modestbranding=0&cc_load_policy=0&iv_load_policy=3&enablejsapi=1&version=3`;
	const vimeoSpecificParams = `&api=1&title=0&byline=0&portrait=0&sidedock=0&autopip=none&pip=none&byline=0&responsive=1`;

	return `?${autoplay}${loop}${dnt}${muted}${playsinline}${controls}${ isVimeo.value ? vimeoSpecificParams : youtubeSpecificParams }`;
});

const embedUrl = () => {
	if (!embedVideoId?.value) return null;
	if (isVimeoInPopup.value) return `${embedTemplate}${embedVideoId.value}`;

	return `${embedTemplate}${embedVideoId.value}${embedVideoParams.value}`;
};

function runVideo(event) {
	setTimeout(() => {
		if (!isVimeo.value) {
			videoPlayer.playVideo(event);
		} else {
			videoPlayer.play(event);
		}
	}, 200);
}

function onPlayerReady(event) {
	runVideo(event);
	isOnReadySent.value = true;
}

function onYouTubeIframeAPIReady(video) {
	videoPlayer = new YT.Player(video, {
		events: {
			onReady: onPlayerReady
		}
	});
	isPlayerCreated.value = true;
}

function onVimeoPlayerLoaded(video) {
	videoPlayer = new Vimeo.Player(video);
	videoPlayer.on('loaded', () => {
		isVideoPlaying.value = isVideoPlayingOnLoad;
		if (isVideoPlayingOnLoad) onPlayerReady();
	});
	isPlayerCreated.value = true;
}

function playVideo(event) {
	event?.preventDefault();

	if (isVideoPaused.value) {
		isVideoPaused.value = false;
		runVideo(event);

		return
	}

	isVideoPlaying.value = true;
	videoUrl.value = props.videoUrl;
	track_content_engagement('video', 'start', props.title || 'no_data', videoUrl.value);

	if (!isVideoPlayingOnLoad) return false;

	setTimeout(() => {
		if (!isVimeo.value) {
			onYouTubeIframeAPIReady(video.value);
		} else {
			onVimeoPlayerLoaded(video.value);
		}

		if (!props.videoLoop || isVimeoInPopup.value) return false;

		// added due to delay of loading methods
		intervalTimer.value = setInterval(() => {
			isPlayerFullyLoaded.value = Boolean(videoPlayer?.pauseVideo || videoPlayer?.pause);

			if (isPlayerFullyLoaded.value) clearInterval(intervalTimer.value);
		}, 300)
	}, 100);
}

function pauseVideo (event) {
	event.preventDefault();

	isVideoPaused.value = true;
	if (!isVimeo.value) {
		videoPlayer.pauseVideo();
	}
	else {
		videoPlayer.pause();
	}
}

function closePopup() {
	if (isVimeoInPopup.value && isVideoPlaying.value) {
		isVideoPlaying.value = false;
		videoUrl.value = null;
	}
}

function loadScript() {
	const id = isVimeo.value ? 'iframe-vimeo' : 'iframe-youtube';

	if (document.getElementById(id) && areScriptsLoaded.value) {
		if (isVideoPlayingOnLoad) playVideo();

		return false;
	}

	let videoApi = document.createElement('script');
	videoApi.id = id;
	videoApi.src = isVimeo.value ? 'https://player.vimeo.com/api/player.js' : 'https://www.youtube.com/iframe_api';
	videoApi.type = 'text/javascript';
	videoApi.async = true;

	videoApi.addEventListener('load', () => {
		areScriptsLoaded.value = true;
		if (isVideoPlayingOnLoad) playVideo();
	});

	const firstScriptTag = document.getElementsByTagName('script')[0];
	firstScriptTag?.parentNode.insertBefore(videoApi, firstScriptTag);
}

function onKeydown(event) {
	if (event.key == 'Escape') {
		closePopup();
	}
}

onMounted(() => {
	const isAlreadyYoutubeScript = document.querySelectorAll('script[src="https://www.youtube.com/iframe_api"]').length == 0 && !isVimeo.value;
	const isAlreadyVimeoScript = document.querySelectorAll('script[src="https://player.vimeo.com/api/player.js"]').length == 0 && isVimeo.value;

	if (isAlreadyYoutubeScript || isAlreadyVimeoScript) {
		loadScript();
	}

	document.addEventListener('keydown', onKeydown);

	if (!props.videoLoop) return false;
});

onBeforeUnmount(() => {
	document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
	<div
		class="cms-content-video"
		:class="{
			'video-is-playing': isVideoPlaying,
			'object-contain': objectContain,
		}"
	>
		<div
			:class="{
				'video-overlay': isVimeoInPopup,
			}"
			v-on:click.prevent="closePopup"
		>
			<div class="iframe-wrapper" >
				<iframe
					:id="uniqueId"
					ref="video"
					:title="videoTitle"
					:src="embedUrl()"
					tabindex="-1"
					:allow="[
						isVimeoInPopup
							? 'autoplay; fullscreen'
							: 'fullscreen; accelerometer; encrypted-media; gyroscope; picture-in-picture',
					]"
					:data-player="isVimeo && props.videoLoop ? 'vimeo-in-place' : ''"
					data-testid="yt-video"
				/>
				<span
					v-if="isVimeoInPopup && isVideoPlaying"
					class="video-overlay-close-button"
				>
					<DsIconButton
						icon-name="close"
						v-on:click.prevent="closePopup"
						data-testid="close-video"
					/>
				</span>

				<DsIconButton v-if="showPlayLoopButton"
					:aria-describedby="uniqueId"
					:ariaLabel="playAriaLabel"
					icon-name="play"
					tabindex="0"
					variant="secondary"
					class="c-video-item__videocta"
					:clickHandler="(event) => playVideo(event)" />

				<DsIconButton v-if="showPauseLoopButton"
					:aria-describedby="uniqueId"
					:ariaLabel="pauseAriaLabel"
					icon-name="pause"
					tabindex="0"
					variant="secondary"
					class="c-video-item__videocta"
					:clickHandler="(event) => pauseVideo(event)" />
			</div>
		</div>

		<img
			:src="imageUrl"
			:class="{
				'rounded-corner': imageRoundedCorner,
				'hide-on-mobile': mobileImageUrl,
			}"
			loading="lazy"
			:alt="imageAlt"
			data-testid="yt-image-thumbnail"
		/>
		<img
			v-if="mobileImageUrl"
			:src="mobileImageUrl"
			:alt="imageAlt"
			loading="lazy"
			class="hide-on-desktop"
			data-testid="yt-mobile-image-thumbnail"
		/>

		<template v-if="!isVideoPlaying">
			<DsButton v-if="videoPlayButtonLabel"
				variant="primary-inverted"
				class="c-video-item__videocta--text"
				icon-start="button-play"
				:click-handler="playVideo">
				{{ videoPlayButtonLabel }}
			</DsButton>

			<!-- keeped navigate-forward for the existing blocks -->
			<DsButtonPlay
				v-else
				:ariaLabelPlay="playAriaLabel"
				size="lg"
				:playHandler="playVideo"
				data-testid="yt-play-button">
			</DsButtonPlay>

		</template>

	</div>
</template>

<style lang="scss" scoped>
@import '../../../styles/settings/settings';
@import '../../../styles/tools/tools';

$aspectRationIframeWidth: calc(100% * 1.771); //16 : 9 (1.771 : 1)
$leftIframePosition: -38.55%; // (77.1% / 2)
.cms-content-video {
	position: relative;
	display: flex;
	margin: 0;
	width: 100%;
	overflow: hidden;

	@media (max-width: ds-breakpoint("md")) {
		height: 100%;
	}

	&.video-img--bigger {
		height: 450px;

		iframe {
			width: $aspectRationIframeWidth;
			height: $aspectRationIframeWidth;
			left: $leftIframePosition;
		}

		@media (max-width: ds-breakpoint("md")) {
			height: 100%;
		}
	}
}

iframe {
	position: absolute;
	width: 100%;
	height: 100%;
	opacity: 0;
}

img {
	position: relative;
	max-height: 100%;
	width: 100%;
	object-fit: cover;

	&.rounded-corner {
		border-radius: 8px;
	}

	.object-contain & {
		object-fit: contain;
	}
}

.video-is-playing {
	iframe {
		opacity: 1;
		border: none;
		border-radius: ds-space(4);
	}

	img {
		opacity: 0;
		z-index: -1;
	}

	.video-overlay {
		display: flex;
		justify-content: center;
		align-items: center;
		background: #213d4666;
		flex: 1 0 auto;
		position: fixed;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		z-index: 100;

		.iframe-wrapper {
			display: flex;
			justify-content: center;
			align-items: flex-start;
			position: relative;
			max-width: 960px;
			width: calc(100% - ds-space(12));
			background-color: rgba(0, 0, 0, 0.9);
			border-radius: ds-space(4);
		}

		iframe {
			width: 960px;
			position: initial;
			opacity: 1;
			aspect-ratio: 960 / 540;
			max-height: calc(100vh - ds-space(12));
		}
	}
}

.video-overlay-close-button {
	z-index: 101;
	position: absolute;
	top: 0;
	right: 0;
}

@include t-respond-to-min(ds-breakpoint('md')) {
	.hide-on-desktop {
		display: none;
	}
}

@include t-respond-to-max(ds-breakpoint('md')) {
	.hide-on-mobile {
		display: none;
	}
}
</style>

<style lang="scss">
@import '../../../styles/settings/settings';
@import '../../../styles/tools/tools';
.cms-content-video {
	button {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);

		&.c-video-item__videocta {
			top: 100%;
			left: 100%;
			transform: translate(-200%, -200%);

			&--text {
				top: inherit;
				bottom: ds-space("6");
				left: ds-space("6");
				transform: inherit;

				@media (min-width: ds-breakpoint("md")) {
					bottom: ds-space("9");
					left: ds-space("9");
				}
			}
		}
	}

	.ds-icon-button {
		opacity: 0.9;
	}
}
</style>
